import { Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { AuthService } from "@core/services/auth.service";
import { Address, countryDefaultValue } from "@models/address/address";
import { Company } from "@models/companies/company";
import { EntityData } from "@models/companies/entity-data";
import { Document } from "@models/documents/document";
import { EntityChangeData } from "@models/documents/entity-change-data";
import { IndividualData } from '@models/individuals/individual-data';
import { OfficerAppointmentType } from '@models/relationships/officer-appointment-type';
import { Relationship, ShareholderRelationshipDetails } from "@models/relationships/relationship";
import {
  RelationshipType,
  RelationshipTypeLabels,
  RelationshipTypeLabelsPlural
} from "@models/relationships/relationship-type.enum";
import { SecurityTransactionType } from "@models/security-transactions/security-transaction-type.enum";
import { SecurityHolding } from "@models/shares/security-holding";
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord,
} from "@models/shares/security-registry-record";
import { SecurityType } from "@models/shares/security-type";
import { companyRegistrationShareTypesOptions } from "@models/shares/share-type.enum";
import { jurisdictionOptions, JurisdictionType } from "@models/trusts/trusts-enums";
import { SelectOption } from "@models/ui-models/select-options";
import {
  NgbAccordionBody,
  NgbAccordionCollapse,
  NgbAccordionDirective,
  NgbAccordionHeader,
  NgbAccordionItem,
  NgbAccordionToggle
} from "@ng-bootstrap/ng-bootstrap";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { abnDeclarationMessage } from '@shared/constants/abn-declaration-message.constant';
import { ButtonComponent } from "@shared/controls/button/button.component";
import { CheckboxGroupComponent } from "@shared/controls/checkbox-group/checkbox-group.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { FlagsAutocompleteComponent } from "@shared/controls/flags-autocomplete/flags-autocomplete.component";
import { InputNumberComponent } from "@shared/controls/input-number/input-number.component";
import { InputComponent } from "@shared/controls/input/input.component";
import {
  AbnMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/abn-masked-input/abn-masked-input.component";
import {
  DinMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/din-masked-input/din-masked-input.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { SelectComponent } from "@shared/controls/select/select.component";
import { TextareaComponent } from "@shared/controls/textarea/textarea.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import { CountriesService } from '@shared/services/countries.service';
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { SettingsApiService } from "@shared/services/settings-api.service";
import { clearFormArray } from "@shared/utils/clear-form-array";
import { Guid } from "@shared/utils/guid.helper";
import { parseFullName } from "@shared/utils/parse-fullname";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { companyNameValidator } from "@shared/validators/compny-name.validator";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import { directorFullNameValidatorMsg } from '@shared/validators/custom-form-validators-messages';
import { DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { NumbersValidators } from "@shared/validators/numbers.validators";
import {
  reservationNumberCustomErrors,
  reservationNumberValidator
} from '@shared/validators/reservation-number.validator';
import { combineLatest, debounceTime, startWith } from "rxjs";

import {
  ShareJointSecurityHolderComponent
} from "../../../controls/share-joint-security-holder/share-joint-security-holder.component";
import {
  ShareSecurityTypesDropdownComponent
} from "../../../controls/share-security-types-dropdown/share-security-types-dropdown.component";
import { addressIncludesPoBox, poBoxAddressCustomErrors } from "../../../models/company/company-change-address.model";
import { ApplicantType } from '../../../models/company/company-change-deregister.model';
import { legalElements } from "../../../models/company/company-change-name.model";
import {
  CompanyRegistration,
  CompanyStandardStepsEnum,
  OfficeholderFormGroup,
  OfficeholderFormGroupControls,
  SecurityholderFormGroup,
  SecurityHoldingFormGroup,
  SecurityTransactionBalance
} from "../../../models/company/company-registration.model";
import {
  customErrorsOfShareCancellationNumbers,
  totalNumberOfSharesCustomErrors
} from '../../../models/shares/company-security-cancellation.model';
import {
  getNewShareholderTypesSelectOptions,
  ShareholderTypesEnum,
} from "../../../models/shares/company-security-issue.model";
import {
  AddressAndOccupierFormGroupComponent
} from "../../../reusable-form-groups/address-and-occupier-form-group/address-and-occupier-form-group.component";
import {
  AddressFormGroup,
  AddressFormGroupComponent,
  AddressFormGroupControls
} from "../../../reusable-form-groups/address-form-group/address-form-group.component";
import {
  CompanyNameAcnFormGroupComponent,
} from "../../../reusable-form-groups/company-name-acn-form-group/company-name-acn-form-group.component";
import {
  IndividualDataFormGroupComponent,
  IndividualDataFormGroupControls
} from "../../../reusable-form-groups/individual-data-form-group/individual-data-form-group.component";
import {
  AuthorisedSignatoriesStepComponent
} from "../../../reusable-form-parts/authorised-signatories-step/authorised-signatories-step.component";
import { BaseStepperFormComponent, StepperFormComponent } from "../../stepper-form";
import { companyRegistrationSteps } from "./company-registration-constants";
import {
  specialPurposeCompanyDescription,
  standardCompanyDescription
} from "./constants/company-registration-description";
import {
  getCompanyRegistrationOfficersPositionTypesOptions
} from "./utils/get-company-registration-officers-position-types-options";

@Component({
  selector: 'app-company-registration',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ValidationErrorComponent,
    InputComponent,
    RadioComponent,
    CheckboxComponent,
    TextareaComponent,
    SelectComponent,
    YesNoControlComponent,
    ButtonComponent,
    DividerComponent,
    InputNumberComponent,
    AbnMaskedInputComponent,
    StepperFormComponent,
    CompanyNameAcnFormGroupComponent,
    AddressAndOccupierFormGroupComponent,
    AddressFormGroupComponent,
    IndividualDataFormGroupComponent,
    ShareJointSecurityHolderComponent,
    ShareSecurityTypesDropdownComponent,
    NgbAccordionDirective,
    NgbAccordionItem,
    NgbAccordionHeader,
    NgbAccordionCollapse,
    NgbAccordionBody,
    NgbAccordionToggle,
    FlagsAutocompleteComponent,
    CheckboxGroupComponent,
    DinMaskedInputComponent,
    AuthorisedSignatoriesStepComponent,
  ],
  templateUrl: './company-registration.component.html',
  styleUrls: [
    './company-registration.component.scss',
    '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
  ]
})
export class CompanyRegistrationComponent extends BaseStepperFormComponent<CompanyStandardStepsEnum, CompanyRegistration> implements OnInit {
  private countriesService = inject(CountriesService);
  private authService = inject(AuthService);
  private organisationService = inject(OrganisationApiService);
  private settingsService = inject(SettingsApiService);

  CountriesService = CountriesService;

  // Only Special Purpose
  isSpecialPurpose = false;
  isSimpleShareholdersFillingMode = false; // fill shareholders according to officeholders data if true

  // General
  override readonly StepsEnum = CompanyStandardStepsEnum;
  readonly smsfDeclarationConfirmationOptions: SelectOption[] = [{
    label: 'By completing this form, I assent to the following declarations:',
    value: true
  }];
  formHeader = 'Company Standard';
  suggestedNames: string[] = [];

  // CompanyDetails
  readonly abnDeclarationMessage = abnDeclarationMessage;
  readonly ApplicantType = ApplicantType;
  readonly directorFullNameValidatorMsg = directorFullNameValidatorMsg;
  readonly jurisdictionOptions = jurisdictionOptions;
  readonly countries = this.countriesService.getCountries();
  readonly reservationNumberCustomErrors = reservationNumberCustomErrors;
  legalElementsOptions: SelectOption[] = [];
  applicantTypeOptions: SelectOption[] = [
    { label: 'Individual', value: ApplicantType.Director, disabled: false },
    { label: 'Company', value: ApplicantType.Company, disabled: false },
  ];

  // Address
  staticSuggestedAddressesRecord: Record<string, Address | null> = {};
  suggestedAddresses: Address[] = [];

  // Officeholders
  readonly RelationshipType = RelationshipType;
  officeholdersPositionsCustomErrors: Record<string, string> = {};
  firstOfficerCompanyStandardPositionTypesOptions = getCompanyRegistrationOfficersPositionTypesOptions();
  companyStandardPositionTypesOptions = getCompanyRegistrationOfficersPositionTypesOptions();

  // Shareholders
  readonly newShareholderTypesSelectOptions = getNewShareholderTypesSelectOptions();
  readonly ShareholderTypesEnum = ShareholderTypesEnum;
  readonly individualDataFormLabels: Record<keyof Partial<IndividualDataFormGroupControls>, string> = { fullName: 'Individual Name' } as Record<keyof Partial<IndividualDataFormGroupControls>, string>;
  readonly addressFormLabels: Record<keyof Partial<AddressFormGroupControls>, string> = { normalizedFullAddress: 'Registered address' } as Record<keyof Partial<AddressFormGroupControls>, string>;
  readonly hiddenIndividualControls: (keyof IndividualDataFormGroupControls)[] = ['formerName', 'dob', 'birthCity', 'birthCountry'];
  readonly totalNumberOfSharesCustomErrors = totalNumberOfSharesCustomErrors;
  readonly addressesCustomErrors = {
    ...poBoxAddressCustomErrors,
    foreignAddressForAllDirectors: `It's now allowed to select address of all directors to a foreign address.`,
    foreignAddressForAllSecretaries: `It's now allowed to select address of all secretaries to a foreign address.`,
  };
  readonly customErrorsOfShareCancellationNumbers = customErrorsOfShareCancellationNumbers;
  readonly authSignatoriesValidators: ValidatorFn[] = [AuthorisedSignatoriesStepComponent.maxSignatoriesLimitValidator(2)];

  companyRegistrationShareTypesOptions = companyRegistrationShareTypesOptions;
  securityTypes: SecurityType[] = [new SecurityType({
    securityTypeId: Guid.generate(),
    entityId: this.companyChangeData.entityId
  })];
  securityTypesRecord: Record<string, SecurityType> = { [this.securityTypes[0].securityTypeId]: this.securityTypes[0] };
  authorisedSecurityRegistryRecords: SecurityRegistryRecord[] = [];

  override stepperForm = new FormGroup({
    [CompanyStandardStepsEnum.Declaration]: new FormGroup({}),
    [CompanyStandardStepsEnum.CompanyDetails]: new FormGroup({
      // Company Name
      companyName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(3), companyNameValidator()]),
      useAcnAsNewCompanyName: new FormControl<boolean | null>(false, [Validators.required]),
      newLegalElementsIfAcnIsUsed: new FormControl<string | null>(null, [Validators.required]),
      jurisdiction: new FormControl<JurisdictionType | null>(null, [Validators.required]),
      isProposedNameIdenticalToRegisteredBusinessName: new FormControl<boolean | null>(false, [Validators.required]),
      abnConfirmation: new FormControl<boolean | null>(false, [Validators.requiredTrue]),
      businessNameHolderAbn: new FormControl<string | null>(null),
      manualReviewRequested: new FormControl<boolean | null>(false, [Validators.required]),
      manualProcessingReason: new FormControl<string | null>(null, [Validators.required]),
      newNameReservedWithForm410: new FormControl<boolean | null>(false, [Validators.required]),
      reservationNumber: new FormControl<string | null>(null, [Validators.required, reservationNumberValidator]),
      applicantType: new FormControl<ApplicantType | null>(null),
      applicantCompanyName: new FormControl<string | null>(null),
      applicantIndividualName: new FormControl<string | null>(null),

      // Ultimate Holding Company
      haveUltimateHoldingCompany: new FormControl<boolean | null>(null, [Validators.required]),
      ultimateHoldingCompany: new FormGroup({
        ...CompanyNameAcnFormGroupComponent.defineFormControls(),
        aboardCompanyName: new FormControl<string | null>(null, [Validators.required]),
        ultimateHoldingCompanyCountry: new FormControl<string | null>(null, Validators.required),
      }),
    }),
    [CompanyStandardStepsEnum.Address]: new FormGroup({
      principalAddressSameAsRegisteredAddress: new FormControl<boolean | null>(false, [Validators.required]),
      registeredAddressAndOccupier: AddressAndOccupierFormGroupComponent.defineForm(),
      principalAddress: AddressFormGroupComponent.defineForm(),
    }),
    [CompanyStandardStepsEnum.Officeholders]: new FormGroup({
      officers: new FormArray<OfficeholderFormGroup>([])
    }),
    [CompanyStandardStepsEnum.Shareholders]: new FormGroup({
      securityHolders: new FormArray<SecurityholderFormGroup>([])
    }),
    [CompanyStandardStepsEnum.AuthorisedSignatories]: AuthorisedSignatoriesStepComponent.defineForm(),
  });

  constructor() {
    super();
    this.updateFormSteps();
    this.redirectAfterSubmit = true;
  }

  ngOnInit(): void {
    this.legalElementsOptions = this.getLegalElementsOptions();
    this.setupFormVariant(this.formModel.isSpecialPurpose ?? false);

    this.listenControlsChange();
    this.setupValidators();

    this.companyDetailsForm.controls.applicantType.setValue(ApplicantType.Director);

    this.setupChange();
    this.fillStaticAddresses();
    this.updateSuggestedFullNames();

    if (!this.companyDetailsForm.controls.haveUltimateHoldingCompany.value) {
      this.companyDetailsForm.controls.haveUltimateHoldingCompany.setValue(false);
    }

    if (this.isSpecialPurpose && this.isEdit) {
      this.setIsSimpleShareholdersFillingMode(false);
    }
  }

  override setCurrentStep(newStepIndex: number): void {
    super.setCurrentStep(newStepIndex);
    this.updateFormSteps();

    if (this.currentStep == this.StepsEnum.Shareholders) {
      this.prefillSMSFShareholders();
    }

    if (this.currentStep == this.StepsEnum.AuthorisedSignatories) {
      this.fillAuthorisedSignatories();
    }
  }

  override afterSubmit(changes: EntityChangeData[]) {
    const companyRegistrationChange = changes.find((change) => change.$type === CompanyRegistration.$type) as CompanyRegistration;
    this.setupChange(companyRegistrationChange);
  }

  override setupChange(change: CompanyRegistration = this.formModel) {
    this.addressForm.controls.registeredAddressAndOccupier.controls.address.patchValue({ country: countryDefaultValue });
    this.addressForm.controls.principalAddress.patchValue({ country: countryDefaultValue });
    this.companyDetailsForm.controls.abnConfirmation.setValue(this.isEdit);

    if (!this.isEdit && change === this.formModel) return;

    this.setupCompanyDetailsChange(change);
    this.setupAddressChange(change);
    this.setupOfficeholdersChange(change);
    this.setupSecurityholdersChange(change);

    if (!this.companyDetailsForm.controls.haveUltimateHoldingCompany.value) {
      this.companyDetailsForm.controls.haveUltimateHoldingCompany.setValue(false);
    }
  }

  override buildDocument(): Document | null {
    const change = new CompanyRegistration({
      ...this.buildCompanyDetailsPayload(),
      ...this.buildAddressPayload(),
      ...this.buildOfficersPayload(),
      ...this.buildShareholdersPayload(),
      changeDate: DatepickerHelper.getToday(),
      isSpecialPurpose: this.isSpecialPurpose,
    });

    try {
      return new Document({
        changes: [change],
        entityId: this.companyChangeData.entityId || null, // TODO: specify about the entity id
        type: 'c:201',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  updateFormSteps(): void {
    this.steps = companyRegistrationSteps.filter((step) => {

      if (step.step == CompanyStandardStepsEnum.AuthorisedSignatories) {
        return this.isAuthorisedSignatoriesStepVisible();
      }

      return true;
    });
  }

  isAuthorisedSignatoriesStepVisible(): boolean {
    const { securityHoldings } = this.buildShareholdersPayload();

    const someHolderIsCorporateHolder = securityHoldings?.some((securityHolding) => {
      return securityHolding.relationships?.some((holder) => holder.entityDataOverride);
    });

    return someHolderIsCorporateHolder ?? false;
  }

  fillAuthorisedSignatories(): void {
    this.authorisedSecurityRegistryRecords = this.shareholdersForm.controls.securityHolders.value
      .map((shareholderForm) => {
        let holders: (IndividualHolderModel | CorporateHolderModel)[] = [];
        const holderType = shareholderForm.shareholderType;

        if (holderType === ShareholderTypesEnum.Corporate) {
          const entityData = new EntityData({
            name: shareholderForm.name ?? '',
            entityNumber: shareholderForm.acn ?? '',
            registeredAddress: new Address(shareholderForm.registeredAddress as Partial<Address>),
          });
          const holder = new CorporateHolderModel({
            entityData,
            relationshipId: shareholderForm.relationshipId || Guid.generate()
          });
          holder.details.authorisedSignatories = shareholderForm.authSignatories ?? [];
          holders = [holder];
        } else if (shareholderForm.joint) {
          holders = shareholderForm.joint
            .filter((holder) => holder.$type === CorporateHolderModel.$type)
            .map((holder) => {

              holder.relationshipId = holder.relationshipId || Guid.generate();
              return holder as CorporateHolderModel;
            });
        }

        return new SecurityRegistryRecord({ holders });
      });
  }

  onAuthSignatoriesChange(signRecord: Record<string, IndividualData[]>): void {
  }

  onCorporateHolderChangeChange(holder: CorporateHolderModel): void {
    // change of auth signatories for shareholder type = corporate holder
    const shareholderFormToUpdate = this.shareholdersForm.controls.securityHolders.controls.find((shareholderForm) => {
      return shareholderForm.controls.relationshipId.value == holder.relationshipId;
    });
    shareholderFormToUpdate?.controls.authSignatories.setValue(holder.details.authorisedSignatories ?? null);

    // change of auth signatories for shareholder type = joint corporate holder
    this.shareholdersForm.controls.securityHolders.controls
      .forEach((shareholderForm) => {
        if (shareholderForm.controls.shareholderType.value == ShareholderTypesEnum.JointShareholder) {
          const value = shareholderForm.controls.joint.value;
          const relatedCorporateHolderIndex = value?.findIndex((h) => h.relationshipId == holder.relationshipId);

          if (relatedCorporateHolderIndex != undefined && relatedCorporateHolderIndex > -1 && value && value[relatedCorporateHolderIndex]) {
            value[relatedCorporateHolderIndex].details.authorisedSignatories = holder.details.authorisedSignatories;
            shareholderFormToUpdate?.controls.joint.setValue(value);
          }
        }
      });
  }

  onHoldingCompanySelected(company: Company): void {
    this.companyDetailsForm.controls.ultimateHoldingCompany.patchValue({
      name: company?.name,
      acn: company?.acn,
      ultimateHoldingCompanyCountry: company?.registeredAddress?.country || countryDefaultValue
    });
  }

  onCorporateShareholderSelected(company: Company, shareholderFormGroup: SecurityholderFormGroup): void {
    shareholderFormGroup.patchValue({
      name: company?.name,
      acn: company?.acn,
      isOverseasCompany: !CountriesService.isAustralianStateOrAustralia(company?.registeredAddress?.country),
      registeredAddress: company?.registeredAddress,
    });
  }

  listenControlsChange(): void {
    this.listenCompanyDetailsControls();
    this.listenAddressControls();
  }

  setupValidators(): void {
    this.addDoNotAllowForeignAddressForAllDirectorsValidator();
    this.addDoNotAllowForeignAddressForAllSecretariesValidator();
    this.addPoBoxAddressValidator(this.addressForm.controls.principalAddress);
    this.addPoBoxAddressValidator(this.addressForm.controls.registeredAddressAndOccupier.controls.address);
    this.addOfficeholderPositionValidator(RelationshipType.Director);

    this.companyStandardPositionTypesOptions.forEach((positionOption) => this.addOfficeholderPoBoxAddressValidator(positionOption.value as OfficerAppointmentType));
  }

  // Company Details
  listenCompanyDetailsControls(): void {
    this.listenUseAcnAsNewCompanyNameChange();
    this.listenHasReservationBy410Change();
    this.listenIsProposedNameIdenticalToBusinessNameChange();
    this.listenManualReviewRequestedChange();
    this.listenApplicantTypeChange();
    this.listenCompanyHasUhcChange();
    this.listenJurisdictionChange();
  }

  listenUseAcnAsNewCompanyNameChange(): void {
    this.companyDetailsForm.controls.useAcnAsNewCompanyName.valueChanges
      .pipe(startWith(false), takeUntilDestroyed(this.destroyRef))
      .subscribe((useAcnAsNewCompanyName) => {
        setControlDisabled(this.companyDetailsForm.controls.newLegalElementsIfAcnIsUsed, useAcnAsNewCompanyName === false);

        setControlDisabled(this.companyDetailsForm.controls.reservationNumber, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.companyName, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.newNameReservedWithForm410, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.isProposedNameIdenticalToRegisteredBusinessName, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.abnConfirmation, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.businessNameHolderAbn, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.manualReviewRequested, useAcnAsNewCompanyName === true);
        setControlDisabled(this.companyDetailsForm.controls.manualProcessingReason, useAcnAsNewCompanyName === true);

        this.companyDetailsForm.controls.newNameReservedWithForm410.patchValue(this.companyDetailsForm.value.newNameReservedWithForm410 ?? false);
        this.companyDetailsForm.controls.isProposedNameIdenticalToRegisteredBusinessName.patchValue(this.companyDetailsForm.value.isProposedNameIdenticalToRegisteredBusinessName ?? false);
        this.companyDetailsForm.controls.manualReviewRequested.patchValue(this.companyDetailsForm.value.manualReviewRequested ?? false);
      });
  }

  listenHasReservationBy410Change(): void {
    this.companyDetailsForm.controls.newNameReservedWithForm410.valueChanges
      .pipe(startWith(false), takeUntilDestroyed(this.destroyRef))
      .subscribe((hasReservationBy410) => {
        setControlDisabled(this.companyDetailsForm.controls.reservationNumber, hasReservationBy410 !== true);
        setControlDisabled(this.companyDetailsForm.controls.applicantCompanyName, hasReservationBy410 !== true);
        setControlDisabled(this.companyDetailsForm.controls.applicantIndividualName, hasReservationBy410 !== true);
      });
  }

  listenIsProposedNameIdenticalToBusinessNameChange(): void {
    this.companyDetailsForm.controls.isProposedNameIdenticalToRegisteredBusinessName.valueChanges
      .pipe(startWith(false), takeUntilDestroyed(this.destroyRef))
      .subscribe((isProposedNameIdenticalToBusinessName) => {
        setControlDisabled(this.companyDetailsForm.controls.abnConfirmation, isProposedNameIdenticalToBusinessName !== true);
        setControlDisabled(this.companyDetailsForm.controls.businessNameHolderAbn, isProposedNameIdenticalToBusinessName !== true);
      });
  }

  listenManualReviewRequestedChange(): void {
    this.companyDetailsForm.controls.manualReviewRequested.valueChanges
      .pipe(startWith(this.companyDetailsForm.value.manualReviewRequested), takeUntilDestroyed(this.destroyRef))
      .subscribe((manualReviewRequested) => {
        setControlDisabled(this.companyDetailsForm.controls.manualProcessingReason, manualReviewRequested !== true);
      });
  }

  listenApplicantTypeChange(): void {
    this.companyDetailsForm.controls.applicantType.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(applicantType => {
      const applicantCompanyName = this.companyDetailsForm.controls.applicantCompanyName;
      const applicantIndividualName = this.companyDetailsForm.controls.applicantIndividualName;

      if (applicantType === ApplicantType.Company) {
        applicantCompanyName.setValidators([Validators.required, Validators.minLength(3), companyNameValidator()]);
        applicantIndividualName.patchValue(null, { emitEvent: false });
        applicantIndividualName.clearValidators();
      } else if (applicantType === ApplicantType.Director) {
        applicantIndividualName.setValidators([Validators.required, CustomFormValidators.fullNameValidator]);
        applicantCompanyName.patchValue(null, { emitEvent: false });
        applicantCompanyName.clearValidators();
      }

      applicantCompanyName.updateValueAndValidity();
      applicantIndividualName.updateValueAndValidity();
    });
  }

  listenCompanyHasUhcChange(): void {
    this.companyDetailsForm.controls.haveUltimateHoldingCompany.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((companyHasUhc) => {
        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany, companyHasUhc !== true);
        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.aboardCompanyName, companyHasUhc !== true);
        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.name, companyHasUhc !== true);
        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.acn, companyHasUhc !== true);

        if (companyHasUhc)
          this.companyDetailsForm.controls.ultimateHoldingCompany.controls.ultimateHoldingCompanyCountry.setValue(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.ultimateHoldingCompanyCountry.value);
      });
  }

  listenJurisdictionChange(): void {
    this.companyDetailsForm.controls.ultimateHoldingCompany.controls.ultimateHoldingCompanyCountry.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((country) => {
        const isAustraliaSelected = CountriesService.isAustralia(country);

        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.acn, !isAustraliaSelected);
        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.name, !isAustraliaSelected);
        setControlDisabled(this.companyDetailsForm.controls.ultimateHoldingCompany.controls.aboardCompanyName, isAustraliaSelected);
      });
  }

  setupCompanyDetailsChange(change: CompanyRegistration): void {
    const changeUhcCountry = change.ultimateHoldingCompanyCountry?.toLowerCase();
    const countryName = this.countries.find(
      country => country.value?.toString()?.toLowerCase() === changeUhcCountry
    );

    this.companyDetailsForm.patchValue({
      ...change,
      jurisdiction: change.jurisdiction,
      applicantType: change.reservationApplicantIfCompany ? ApplicantType.Company : ApplicantType.Director,
      applicantIndividualName: change.reservationApplicantIfPerson?.fullName,
      applicantCompanyName: change.reservationApplicantIfCompany,
      ultimateHoldingCompany: {
        ...change,
        ultimateHoldingCompanyCountry: (countryName?.value as string) ?? '',
        aboardCompanyName: change.ultimateHoldingCompanyName,
        name: change.ultimateHoldingCompanyName,
        acn: change.ultimateHoldingCompanyAcnOrAbn,
      }
    });
  }

  buildCompanyDetailsPayload(): Partial<CompanyRegistration> {
    const formValue = this.companyDetailsForm.value;
    const countryName = formValue.ultimateHoldingCompany?.ultimateHoldingCompanyCountry ?? '';

    return {
      ...formValue,
      ...formValue.ultimateHoldingCompany,
      reservationApplicantIfCompany: formValue.applicantCompanyName,
      reservationApplicantIfPerson: formValue.applicantIndividualName ? new IndividualData(parseFullName(formValue.applicantIndividualName)) : null,
      ultimateHoldingCompanyAcnOrAbn: formValue.ultimateHoldingCompany?.acn,
      ultimateHoldingCompanyName: formValue.ultimateHoldingCompany?.name ?? formValue.ultimateHoldingCompany?.aboardCompanyName,
      ultimateHoldingCompanyCountry: countryName
    } as Partial<CompanyRegistration>;
  }

  // Address
  listenAddressControls(): void {
    this.listenPrincipalAddressSameAsRegisteredAddressChange();
    this.listenCompanyAddressesChange();
  }

  listenPrincipalAddressSameAsRegisteredAddressChange(): void {
    this.addressForm.controls.principalAddressSameAsRegisteredAddress.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isSame) => {
        setControlDisabled(this.addressForm.controls.principalAddress, !!isSame);
      });
  }

  listenCompanyAddressesChange(): void {
    this.addressForm.controls.registeredAddressAndOccupier.controls.address.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedAddresses());

    this.addressForm.controls.principalAddress.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedAddresses());
  }

  setupAddressChange(change: CompanyRegistration): void {
    this.addressForm.patchValue({
      principalAddressSameAsRegisteredAddress: change.registeredAddress.isEqual(change.principalAddress),
      principalAddress: { ...change.principalAddress, country: countryDefaultValue },
      registeredAddressAndOccupier: {
        ...change,
        address: { ...change.registeredAddress, country: countryDefaultValue },
      },
    });
  }

  buildAddressPayload(): Partial<CompanyRegistration> {
    const formValue = this.addressForm.value;
    return {
      registeredAddress: formValue.registeredAddressAndOccupier!.address as Address,
      principalAddress: (formValue.principalAddressSameAsRegisteredAddress ? formValue.registeredAddressAndOccupier!.address : formValue!.principalAddress) as Address,
      occupiesTheAddress: formValue.registeredAddressAndOccupier!.occupiesTheAddress!,
      occupierNameIfDoesntOccupy: formValue.registeredAddressAndOccupier?.occupierNameIfDoesntOccupy,
    };
  }

  // Officeholders
  createOfficeholderFormGroup(officeholder: Partial<Relationship> = {}): OfficeholderFormGroup {
    const type = officeholder.type !== null && officeholder.type !== undefined ? [officeholder.type] : [];
    const officeholderFormGroup = new FormGroup<OfficeholderFormGroupControls>({
      ...IndividualDataFormGroupComponent.defineFormControls(),
      individualId: new FormControl<string | null>(officeholder.individualId ?? Guid.generate()),
      type: new FormControl<RelationshipType[]>([], { validators: [Validators.required], nonNullable: true }),
      din: new FormControl<string | null>(null),
    });

    if (!officeholder?.relationshipId && this.isSpecialPurpose) {
      // is new officeholder in special purpose form

      type.push(...this.companyStandardPositionTypesOptions.map((relationshipTypeOption) => relationshipTypeOption.value as RelationshipType));
    }

    this.listenOfficeholderChangeFullName(officeholderFormGroup);
    this.listenOfficeholderChangeAddress(officeholderFormGroup);

    officeholderFormGroup.patchValue({
      ...officeholder,
      ...officeholder.individualDataOverride,
      type,
      formerFullName: officeholder.individualDataOverride?.formerName.fullNameWithMiddle ?? '',
    });

    return officeholderFormGroup;
  }

  addOfficeholder(officeholder: Partial<Relationship> = {}): void {
    this.officeholdersForm.controls.officers.push(this.createOfficeholderFormGroup(officeholder));
  }

  removeOfficeholder(index: number): void {
    this.officeholdersForm.controls.officers.removeAt(index);

    setTimeout(() => {
      // Add "Public Officer" position for first officer if it's not selected yet
      const newFirstOfficerTypes = this.officeholdersForm.controls.officers.at(0).controls.type.value;

      if (!newFirstOfficerTypes.includes(RelationshipType.PublicOfficer))
        this.officeholdersForm.controls.officers.at(0).controls.type.setValue([RelationshipType.PublicOfficer, ...newFirstOfficerTypes]);
    });
  }

  listenOfficeholderChangeFullName(officeholderFormGroup: OfficeholderFormGroup): void {
    officeholderFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedFullNames());
  }

  listenOfficeholderChangeAddress(officeholderFormGroup: OfficeholderFormGroup): void {
    officeholderFormGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedAddresses());
  }

  setupOfficeholdersChange(change: CompanyRegistration): void {
    clearFormArray(this.officeholdersForm.controls.officers);

    const relationshipsByTypes = change.officers.reduce((relationships, officer) => {
      const id = officer.individualId ?? officer.relationshipId;

      if (relationships[id]) {
        relationships[id].push(officer);
      } else {
        relationships[id] = [officer];
      }

      return relationships;
    }, {} as Record<string, Relationship[]>);

    change.securityHoldings.forEach(holding => {
      if (holding.relationships?.length !== 1) return;
      if (!holding.relationships[0].individualId) return;

      const matchedOfficer = relationshipsByTypes[holding.relationships[0].individualId];

      if (matchedOfficer) {
        matchedOfficer.push(holding.relationships[0]);
      }
    });

    const officersFormGroups: OfficeholderFormGroup[] = Object.values(relationshipsByTypes)
      .reduce((forms, relationships) => {
        const officeholderFormGroup = this.createOfficeholderFormGroup(relationships[0]);
        const type = relationships.reduce((types, relationship) => [...types, relationship.type], [] as RelationshipType[]);

        officeholderFormGroup.patchValue({ type });
        forms.push(officeholderFormGroup);

        return forms;
      }, [] as OfficeholderFormGroup[]);

    officersFormGroups.forEach((officeholderFormGroup: OfficeholderFormGroup) => {
      this.officeholdersForm.controls.officers.push(officeholderFormGroup);
    });
  }

  buildOfficersPayload(): Partial<CompanyRegistration> {
    const officers: Relationship[] = this.officeholdersForm.controls.officers.controls.reduce((relationships, officeholderFormGroup) => {
      const individualId = officeholderFormGroup.controls.individualId.value ?? Guid.generate();
      const individualDataOverride = IndividualDataFormGroupComponent.toIndividualData(officeholderFormGroup as unknown as FormGroup<Partial<IndividualDataFormGroupControls>>);
      const types = officeholderFormGroup.controls.type.value;
      individualDataOverride.din = officeholderFormGroup.controls.din.value ?? '';

      types.filter(t => t !== RelationshipType.Securityholder).forEach((type) => {
        const officer = new Relationship({
          individualDataOverride,
          type,
          individualId
        });

        relationships.push(officer);
      });

      return relationships;
    }, [] as Relationship[]);
    return { officers };
  }

  checkIfItIsSimpleShareholdersFillingMode(change: CompanyRegistration): void {
    if (!this.isSpecialPurpose && !this.isSimpleShareholdersFillingMode || this.isEdit) {
      // false if
      // - the form is not for special purpose company
      // - false if user turned the simple shareholders filling mode off

      this.isSimpleShareholdersFillingMode = false;
      return;
    }

    // true if all shareholders are officeholders
    const officeholdersIndividualIds = change.officers.map((officeholder) => officeholder.individualId);
    const shareholdersIndividualIds = change.securityHoldings
      .flatMap((holding) => holding.relationships)
      .map((shareholder) => shareholder?.individualId);

    this.isSimpleShareholdersFillingMode = shareholdersIndividualIds
      .every((shareholderIndividualId) => shareholderIndividualId && officeholdersIndividualIds.includes(shareholderIndividualId));
  }

  setIsSimpleShareholdersFillingMode(isSimpleShareholdersFillingMode: boolean): void {
    this.isSimpleShareholdersFillingMode = isSimpleShareholdersFillingMode;

    if (isSimpleShareholdersFillingMode) return;

    // Enable shareholder type and set it's value to itself
    // the rest of job performs valueChanges listeners of form
    this.shareholdersForm.controls.securityHolders.controls.forEach((shareholderFormGroup) => {
      shareholderFormGroup.controls.securityTypes.enable();
      shareholderFormGroup.controls.isNotBeneficialOwner.enable();
    });
  }

  // Shareholders
  addShareType(shareholderIndex: number, securityHoldingIndex: number, newSecurityHolding: SecurityType): void {
    newSecurityHolding.entityId = this.companyChangeData.entityId;
    this.securityTypes.push(newSecurityHolding);
    this.securityTypesRecord[newSecurityHolding.securityTypeId] = newSecurityHolding;
    this.shareholdersForm.controls.securityHolders
      .controls[shareholderIndex]
      .controls.securityTypes.controls[securityHoldingIndex]
      .controls.securityTypeId?.setValue(newSecurityHolding.securityTypeId);
  }

  createSecurityTypeFormGroup(securityHolding: SecurityHolding | null = null): SecurityHoldingFormGroup {
    const securityHoldingFormGroup = new FormGroup({
      securityTypeId: new FormControl<string | null>(securityHolding?.securityType?.securityTypeId ?? null, [Validators.required]),
      number: new FormControl<number | null>(securityHolding?.number ?? null, [Validators.required, NumbersValidators.min(0), NumbersValidators.isInteger]),
      paid: new FormControl<number | null>(securityHolding?.paid ?? null, [Validators.required, NumbersValidators.min(0)]),
      unpaid: new FormControl<number | null>(securityHolding?.unpaid ?? null, [Validators.required, NumbersValidators.min(0)])
    });

    this.setCustomSecurityTypeFormValidators(securityHoldingFormGroup);
    this.listenShareholdingNumberChange(securityHoldingFormGroup);

    return securityHoldingFormGroup;
  }

  createSecurityholderFormGroup(): SecurityholderFormGroup {
    const securityholderFormGroup = new FormGroup({
      shareholderType: new FormControl<ShareholderTypesEnum | null>(null, [Validators.required]),
      isNotBeneficialOwner: new FormControl<boolean | null>(false, [Validators.required]),
      beneficialOwner: new FormControl<string | null>(null, [Validators.required]),

      individualId: new FormControl<string | null>(null),
      individualShareholder: IndividualDataFormGroupComponent.defineForm(),

      ...CompanyNameAcnFormGroupComponent.defineFormControls(),
      isOverseasCompany: new FormControl<boolean | null>(null),
      registeredAddress: AddressFormGroupComponent.defineForm(),
      authSignatories: new FormControl<IndividualData[] | null>([]),

      joint: new FormControl<(IndividualHolderModel | CorporateHolderModel)[] | null>(null, [Validators.required]),

      securityTypes: new FormArray<SecurityHoldingFormGroup>([this.createSecurityTypeFormGroup()]),
      relationshipId: new FormControl<string>(Guid.generate(), { nonNullable: true }),
    });

    this.listenShareholderTypeChanges(securityholderFormGroup);
    this.listenIsBeneficialOwnerChanges(securityholderFormGroup);
    this.listenIndividualShareholderFullNameChanges(securityholderFormGroup);
    this.listenShareholderAddressChanges(securityholderFormGroup);

    return securityholderFormGroup;
  }

  removeShareholdersShareHolding(shareholderIndex: number, shareholdingIndex: number): void {
    this.shareholdersForm.controls.securityHolders.controls[shareholderIndex].controls.securityTypes.removeAt(shareholdingIndex);
  }

  addSecurityHolding(shareholderIndex: number): void {
    this.shareholdersForm.controls.securityHolders.controls[shareholderIndex].controls.securityTypes.push(this.createSecurityTypeFormGroup());
  }

  removeShareholder(shareholderIndex: number): void {
    const individualId = this.shareholdersForm.controls.securityHolders.at(shareholderIndex).controls.individualId.value;

    if (individualId) {
      const relatedOfficerFormGroup = this.officeholdersForm.controls.officers.controls
        .find((officerFormGroup) => officerFormGroup.controls.individualId.value && officerFormGroup.controls.individualId.value == individualId);
      relatedOfficerFormGroup?.controls?.type?.setValue(relatedOfficerFormGroup?.controls?.type?.value?.filter((type) => type !== RelationshipType.Securityholder));
    }

    this.shareholdersForm.controls.securityHolders.removeAt(shareholderIndex);
    this.updateFormSteps();
    this.fillAuthorisedSignatories();
  }

  addShareholder(): void {
    const form = this.createSecurityholderFormGroup();
    this.shareholdersForm.controls.securityHolders.push(form);
  }

  onJointHolderRemoved(): void {
    this.updateFormSteps();
    this.fillAuthorisedSignatories();
  }

  getSelectedSecurityType(securityTypeFormGroup: SecurityHoldingFormGroup): SecurityType | null {
    return this.securityTypesRecord[securityTypeFormGroup.controls.securityTypeId.value!] ?? null;
  }

  setupSecurityholdersChange(change: CompanyRegistration): void {
    this.disabledUnnecessaryControls();
    clearFormArray(this.shareholdersForm.controls.securityHolders);

    const securityTypes: SecurityType[] = [];
    const securityTypesRecord: Record<string, SecurityType> = {};
    const relationshipsGroups: Record<string, Relationship[]> = {};
    const relationshipsGroupsHoldings: Record<string, SecurityHolding[]> = {};

    change.securityHoldings.forEach((securityHolding) => {
      if (!securityTypesRecord[securityHolding.securityType.securityTypeId]) {
        securityTypesRecord[securityHolding.securityType.securityTypeId] = securityHolding.securityType;
        securityTypes.push(securityHolding.securityType);
      }

      const relationshipGroupKey = securityHolding.relationships!.map((r) => r.relationshipId).join();

      if (relationshipsGroupsHoldings[relationshipGroupKey]) {
        relationshipsGroupsHoldings[relationshipGroupKey].push(securityHolding);
      } else {
        relationshipsGroupsHoldings[relationshipGroupKey] = [securityHolding];
        relationshipsGroups[relationshipGroupKey] = securityHolding.relationships!;
      }
    });

    this.securityTypes = securityTypes;
    this.securityTypesRecord = securityTypesRecord;

    Object.entries(relationshipsGroups)
      .forEach(([relationshipGroupKey, relationships]) => {
        const shareholdersForm = this.createSecurityholderFormGroup();
        const relationship = relationships[0];
        const shareholderDetails = relationship?.details as ShareholderRelationshipDetails;

        shareholdersForm.controls.isNotBeneficialOwner.setValue(!shareholderDetails?.isBeneficialOwner);
        shareholdersForm.controls.beneficialOwner.setValue(shareholderDetails?.beneficialOwner);
        clearFormArray(shareholdersForm.controls.securityTypes);

        if (relationships.length > 1) {
          shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.JointShareholder);
          const holders: (IndividualHolderModel | CorporateHolderModel)[] = relationshipsGroups[relationshipGroupKey].map((r) => {
            if (r.individualDataOverride) {
              return new IndividualHolderModel({
                individualId: r.individualId!,
                individualData: r.individualDataOverride,
              });
            }

            const corporateHolder = new CorporateHolderModel({
              entityId: r.entityId,
              entityData: r.entityDataOverride!
            });

            corporateHolder.details.authorisedSignatories = (r?.details as ShareholderRelationshipDetails)?.authorisedSignatories ?? [];

            return corporateHolder;
          });
          shareholdersForm.controls.joint.setValue(holders);
        } else {
          const relationship = relationships[0];
          const relationshipId = relationship.relationshipId;

          if (relationship?.entityDataOverride) {
            shareholdersForm.controls.shareholderType.setValue(ShareholderTypesEnum.Corporate);
            shareholdersForm.patchValue({
              relationshipId,
              name: relationship.entityDataOverride.name,
              acn: relationship.entityDataOverride.entityNumber,
              isOverseasCompany: !CountriesService.isAustralianStateOrAustralia(relationship.entityDataOverride.registeredAddress.country),
              registeredAddress: relationship.entityDataOverride.registeredAddress,
              authSignatories: (relationship.details as ShareholderRelationshipDetails)?.authorisedSignatories ?? [],
            });
          } else {
            shareholdersForm.patchValue({
              relationshipId,
              shareholderType: ShareholderTypesEnum.Individual,
              individualId: relationship.individualId,
            });

            shareholdersForm.controls.individualShareholder.patchValue({
              ...relationship?.individualDataOverride,
              address: relationship?.individualDataOverride?.address,
              formerFullName: relationship.individualDataOverride?.formerName.fullNameWithMiddle ?? ''
            });
          }
        }

        relationshipsGroupsHoldings[relationshipGroupKey].forEach((holding) => {
          const securityTypeFormGroup = this.createSecurityTypeFormGroup(holding);
          shareholdersForm.controls.securityTypes.push(securityTypeFormGroup);
        });

        shareholdersForm.controls.relationshipId.setValue(relationship.relationshipId);
        this.shareholdersForm.controls.securityHolders.push(shareholdersForm);
      });

    this.shareholdersForm.updateValueAndValidity();
  }

  buildShareholdersPayload(): Partial<CompanyRegistration> {
    const securityTypesRelationships: Record<string, Relationship[]> = {};
    const securityHoldings = this.shareholdersForm.controls.securityHolders.controls
      .map((shareholderFormGroup, index) => {
        const shareholderType = shareholderFormGroup.controls.shareholderType.value;
        const relationshipsDetails = new ShareholderRelationshipDetails({
          isBeneficialOwner: !shareholderFormGroup.controls.isNotBeneficialOwner.value,
          beneficialOwner: shareholderFormGroup.controls.beneficialOwner.value!,
        });
        let relationships: Relationship[] = [];
        const relationshipShareHoldings: SecurityHolding[] = [];

        shareholderFormGroup.controls.securityTypes.controls.forEach((securityHoldingForm) => {
          const securityHoldingFormValue = securityHoldingForm.value;
          if (!securityTypesRelationships[securityHoldingForm.controls.securityTypeId.value!]) {
            securityTypesRelationships[securityHoldingForm.controls.securityTypeId.value!] = [];
          }

          switch (shareholderType) {
            case ShareholderTypesEnum.Individual: {
              const individualDataOverride = IndividualDataFormGroupComponent.toIndividualData(shareholderFormGroup.controls.individualShareholder as FormGroup<Partial<IndividualDataFormGroupControls>>);
              relationships = [new Relationship({
                individualDataOverride,
                individualId: shareholderFormGroup.controls.individualId.value,
                relationshipId: shareholderFormGroup.controls.relationshipId.value
              })];
              break;
            }
            case ShareholderTypesEnum.Corporate: {
              relationshipsDetails.authorisedSignatories = shareholderFormGroup.controls.authSignatories.value ?? []

              relationships = [new Relationship({
                relationshipId: shareholderFormGroup.controls.relationshipId.value,
                entityDataOverride: new EntityData({
                  ...shareholderFormGroup.value as Partial<EntityData>,
                  entityNumber: shareholderFormGroup.value.acn!,
                }),
              })];
              break;
            }
            case ShareholderTypesEnum.JointShareholder: {
              relationships = ShareJointSecurityHolderComponent.toRelationships(shareholderFormGroup.value.joint ?? [])
                .map((relationship) => {
                  if (relationship.details) {
                    (relationship.details as ShareholderRelationshipDetails).isBeneficialOwner = !shareholderFormGroup.controls.isNotBeneficialOwner.value;
                    (relationship.details as ShareholderRelationshipDetails).beneficialOwner = shareholderFormGroup.controls.beneficialOwner.value!;
                  }

                  return relationship;
                });
              break;
            }
          }

          relationships = relationships.map((r) => {
            r.type = RelationshipType.Securityholder;
            r.details = shareholderType == ShareholderTypesEnum.JointShareholder ? r.details : relationshipsDetails;
            r.relationshipId = r.relationshipId && r.relationshipId !== Guid.EmptyGuid ? r.relationshipId : Guid.generate();
            return r;
          });

          securityTypesRelationships[securityHoldingFormValue.securityTypeId!] = [
            ...(securityTypesRelationships[securityHoldingFormValue.securityTypeId!] ?? []),
            ...relationships
          ];

          relationshipShareHoldings.push(new SecurityHolding({
            relationships,
            paid: securityHoldingForm.controls.paid.value!,
            unpaid: securityHoldingForm.controls.unpaid.value!,
            number: securityHoldingForm.controls.number.value!,
            securityTypeId: securityHoldingForm.controls.securityTypeId.value!,
            securityType: this.securityTypesRecord[securityHoldingForm.controls.securityTypeId.value!],
          }));
        });

        return relationshipShareHoldings;
      }).flat();

    const balances: SecurityTransactionBalance[] = securityHoldings.map((holding) => {
      const securityTransactionBalance = new SecurityTransactionBalance({
        entityId: null,
        organisationId: this.authService.currentUserProfile()?.organisationId,
        securityTransactionId: Guid.generate(),
        transactionDate: DatepickerHelper.getToday(),
        transactionType: SecurityTransactionType.Balance,
        documentId: this.companyChangeData?.documentId ?? null,
        numberIncrease: holding.number,
        paidIncrease: holding.paid,
        unpaidIncrease: holding.unpaid,
        securityTypeId: holding.securityType.securityTypeId,
        securityType: holding.securityType,
        relationshipIds: holding.relationships?.map((r) => r.relationshipId),
      });

      if (!securityTransactionBalance.securityType.securityTypeId || !securityTransactionBalance.securityType.entityId) {
        delete (securityTransactionBalance.securityType as any).entityId;
        delete (securityTransactionBalance.securityType as any).securityTypeId;
      }

      return securityTransactionBalance;
    });

    return { securityHoldings, balances };
  }

  disabledUnnecessaryControls(): void {
    this.hiddenIndividualControls.forEach((key) => {
      this.shareholdersForm.controls.securityHolders.controls.forEach((shareholderFormGroup) => {
        setControlDisabled(shareholderFormGroup.controls.individualShareholder.controls[key]);
      });
    });
  }

  listenShareholdingNumberChange(securityTypeFormGroup: SecurityHoldingFormGroup): void {
    securityTypeFormGroup.controls.number.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((numberIncrease) => {
        const selectedSecurityType = this.getSelectedSecurityType(securityTypeFormGroup);

        if (numberIncrease === null || selectedSecurityType === null) {
          return;
        }

        securityTypeFormGroup.controls.paid.patchValue(numberIncrease * selectedSecurityType.paidAmount);
        securityTypeFormGroup.controls.unpaid.patchValue(numberIncrease * selectedSecurityType.unpaidAmount);
      });
  }

  listenShareholderTypeChanges(shareholderForm: SecurityholderFormGroup): void {
    shareholderForm.controls.shareholderType.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((shareholderType) => {
        this.updateFormSteps();

        if (shareholderType === null) {
          return;
        }

        // set state of the new Individual Shareholder control
        setControlDisabled(shareholderForm.controls.individualShareholder, shareholderType !== ShareholderTypesEnum.Individual);
        this.hiddenIndividualControls.forEach(key => setControlDisabled(shareholderForm.controls.individualShareholder.controls[key]));

        // set state of the new Corporate Shareholder control
        setControlDisabled(shareholderForm.controls.acn, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(shareholderForm.controls.name, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(shareholderForm.controls.isOverseasCompany, shareholderType !== ShareholderTypesEnum.Corporate);
        setControlDisabled(shareholderForm.controls.registeredAddress, shareholderType !== ShareholderTypesEnum.Corporate);

        // set state of the new Joint Shareholder control
        setControlDisabled(shareholderForm.controls.joint, shareholderType !== ShareholderTypesEnum.JointShareholder);
      });
  }

  listenIsBeneficialOwnerChanges(shareholderForm: SecurityholderFormGroup): void {
    shareholderForm.controls.isNotBeneficialOwner.valueChanges
      .pipe(
        startWith(shareholderForm.controls.isNotBeneficialOwner.value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isSharesOwnedOnBehalf) => {
        setControlDisabled(shareholderForm.controls.beneficialOwner, !isSharesOwnedOnBehalf);
      });
  }

  listenIndividualShareholderFullNameChanges(securityholderFormGroup: SecurityholderFormGroup): void {
    securityholderFormGroup.controls.individualShareholder.controls.fullName.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedFullNames());

    securityholderFormGroup.controls.joint.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedFullNames());
  }

  listenShareholderAddressChanges(securityholderFormGroup: SecurityholderFormGroup): void {
    securityholderFormGroup.controls.registeredAddress.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedAddresses());

    securityholderFormGroup.controls.individualShareholder.controls.address.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedAddresses());

    securityholderFormGroup.controls.joint.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(100))
      .subscribe(() => this.updateSuggestedAddresses());
  }

  private prefillSMSFShareholders(): void {
    if (this.isSpecialPurpose && !this.isEdit) {

      // duplicate all officeholders individual data and prefill
      // with initial shares selection "@ORD $1.00/$0.00" with quantity of 1
      const officeholdersShareholdersFormGroups: OfficeholderFormGroup[] = this.officeholdersForm.controls.officers.controls
        .filter((officeholderFormGroup) => officeholderFormGroup.controls.type.value.includes(RelationshipType.Securityholder));
      const securityHoldersControlsArray = this.shareholdersForm.controls.securityHolders.controls;
      const nonRelatedShareholdersFormGroups = this.shareholdersForm.controls.securityHolders.controls
        .filter((shareholderFormGroup) => !shareholderFormGroup.controls.individualId.value);

      clearFormArray(this.shareholdersForm.controls.securityHolders);
      officeholdersShareholdersFormGroups.forEach((officeholderFormGroup) => {
        const shareholderFormGroup = this.createSecurityholderFormGroup();
        const securityTypes = [{ securityTypeId: this.securityTypes[0].securityTypeId, number: 1 }];

        if (this.isSimpleShareholdersFillingMode && officeholderFormGroup.controls.individualId.value) {
          const existingRelatedFormGroup = securityHoldersControlsArray.find((securityHolderFormGroup) => officeholderFormGroup.controls.individualId.value && officeholderFormGroup.controls.individualId.value == securityHolderFormGroup.controls.individualId.value);

          shareholderFormGroup.patchValue({
            securityTypes: existingRelatedFormGroup?.controls.securityTypes.value ?? securityTypes,
            shareholderType: ShareholderTypesEnum.Individual,
            individualId: existingRelatedFormGroup?.controls.individualId.value ?? officeholderFormGroup.controls.individualId.value,
            individualShareholder: { ...existingRelatedFormGroup?.value, ...officeholderFormGroup.value },
          });
        } else {
          shareholderFormGroup.patchValue({
            securityTypes,
            shareholderType: ShareholderTypesEnum.Individual,
            individualId: officeholderFormGroup.controls.individualId.value,
            individualShareholder: { ...officeholderFormGroup.value },
          });
        }

        this.shareholdersForm.controls.securityHolders.push(shareholderFormGroup);
        setControlDisabled(shareholderFormGroup);

        if (!this.isSimpleShareholdersFillingMode) {
          this.shareholdersForm.controls.securityHolders.controls.forEach((shareholderFormGroup) => {
            shareholderFormGroup.controls.securityTypes.enable();
            shareholderFormGroup.controls.isNotBeneficialOwner.enable();
          });
        }
      });

      nonRelatedShareholdersFormGroups.forEach((shareholderFormGroup) => this.shareholdersForm.controls.securityHolders.push(shareholderFormGroup));
    } else if (!this.shareholdersForm.controls.securityHolders.controls.length) {
      this.shareholdersForm.controls.securityHolders.setControl(0, this.createSecurityholderFormGroup());
    }
  }

  private setCustomSecurityTypeFormValidators(securityTypeFormGroup: SecurityHoldingFormGroup): void {
    const paidTotalValidator: ValidatorFn = () => {
      const selectedSecurityType = this.getSelectedSecurityType(securityTypeFormGroup);
      if (selectedSecurityType === null || !securityTypeFormGroup.value.paid || !securityTypeFormGroup.value.number) {
        return null;
      }
      // (Math.abs(prefilled total paid - total paid))/prefilled total paid < 0.05

      const calculatedTotalPaid = selectedSecurityType.paidAmount * securityTypeFormGroup.value.number;
      return (Math.abs(calculatedTotalPaid - securityTypeFormGroup.value.paid) / calculatedTotalPaid) < 0.05
        ? null
        : { paidChangeIsBiggerThan5Percent: true };
    };

    const unpaidTotalValidator: ValidatorFn = () => {
      const selectedSecurityType = this.getSelectedSecurityType(securityTypeFormGroup);
      if (selectedSecurityType === null || !securityTypeFormGroup.value.unpaid || !securityTypeFormGroup.value.number) {
        return null;
      }
      // (Math.abs(prefilled total unpaid - total unpaid))/prefilled total unpaid < 0.05

      const calculatedTotalPaid = selectedSecurityType.unpaidAmount * securityTypeFormGroup.value.number;
      return (Math.abs(calculatedTotalPaid - securityTypeFormGroup.value.unpaid) / calculatedTotalPaid) < 0.05
        ? null
        : { unpaidChangeIsBiggerThan5Percent: true };
    };

    // total paid + total unpaid > 0
    const paidUnpaidSumValidator: ValidatorFn = () => {
      const totalPaid = securityTypeFormGroup.controls.paid.getRawValue();
      const totalUnpaid = securityTypeFormGroup.controls.unpaid.getRawValue();
      if (totalPaid === null || totalUnpaid === null) {
        return null;
      }

      return (totalPaid + totalUnpaid) > 0
        ? null
        : { paidUnpaidSumIsNotPositive: true };
    };

    securityTypeFormGroup.setValidators([
      paidTotalValidator,
      unpaidTotalValidator,
      paidUnpaidSumValidator
    ]);
  }

  //  do not allow:
  //  - all directors addresses to be a foreign address
  private addDoNotAllowForeignAddressForAllDirectorsValidator(): void {
    this.officeholdersForm.addValidators(((control: AbstractControl) => {
      const form = control['controls']['officers'] as FormArray<OfficeholderFormGroup>;
      const allDirectors = form.controls
        .map((officeholderForm, index) => {

          return {
            country: officeholderForm.controls.address.controls.country.value,
            isDirector: officeholderForm.controls.type.value.includes(RelationshipType.Director)
          };
        })
        .filter(({ isDirector, country }) => isDirector ?? country);

      if (!allDirectors.length)
        return null;

      const allDirectorsHaveOverseasAddress = allDirectors
        .every(({ country }) => !CountriesService.isAustralia(country));

      return allDirectorsHaveOverseasAddress
        ? { foreignAddressForAllDirectors: true }
        : null;
    }) as ValidatorFn);
  }


  //  do not allow:
  //  - all secretaries addresses to be a foreign address
  private addDoNotAllowForeignAddressForAllSecretariesValidator(): void {
    this.officeholdersForm.addValidators(((control: AbstractControl) => {
      const form = control['controls']['officers'] as FormArray<OfficeholderFormGroup>;
      const allSecretaries = form.controls
        .map((officeholderForm, index) => {

          return {
            country: officeholderForm.controls.address.controls.country.value,
            isSecretary: officeholderForm.controls.type.value.includes(RelationshipType.Secretary)
          };
        })
        .filter(({ isSecretary, country }) => isSecretary ?? country);

      if (!allSecretaries.length)
        return null;

      const allSecretariesHaveOverseasAddress = allSecretaries
        .every(({ country }) => !CountriesService.isAustralia(country));

      return allSecretariesHaveOverseasAddress
        ? { foreignAddressForAllSecretaries: true }
        : null;
    }) as ValidatorFn);
  }

  private addPoBoxAddressValidator(addressForm: AddressFormGroup): void {
    const companyPoBoxAddressValidator = (control: AbstractControl): ValidationErrors | null => {
      const form = control as AddressFormGroup;
      const address = new Address(form.value as Partial<Address>);

      return addressIncludesPoBox(address.normalizedFullAddress)
        ? { poBoxAddress: true }
        : null;
    };

    addressForm.addValidators([companyPoBoxAddressValidator]);
  }

  private addOfficeholderPoBoxAddressValidator(relationshipType = OfficerAppointmentType.Director): void {
    const officeholderPoBoxAddressValidator = (control: AbstractControl) => {
      const form = control as FormGroup<{ officers: FormArray<OfficeholderFormGroup> }>;
      const formArray = form.controls.officers;

      // skip validator if
      // - address doesn't include "PO Box"
      const currentTypeOfficeholders = formArray.controls
        .filter((officeholderForm) => officeholderForm.controls.type.value.includes(relationshipType as any as RelationshipType));

      const everyProposedAddressDoesntIncludePoBox = currentTypeOfficeholders
        .every((officeholderForm) => !addressIncludesPoBox(officeholderForm.controls.address.controls.normalizedFullAddress.value ?? ''));

      if (!currentTypeOfficeholders.length || everyProposedAddressDoesntIncludePoBox) {
        return null;
      }

      // valid if at least one officeholder of the relationship type
      // has australian and non po box address
      const atLeastOneHasAuNonPoBoxAddress = currentTypeOfficeholders
        .some((officeholderForm) => {
          const address = officeholderForm.controls.address.controls.normalizedFullAddress.value ?? '';
          return !addressIncludesPoBox(address) && CountriesService.isAustralia(officeholderForm.controls.address.controls.country.value ?? '');
        });

      return atLeastOneHasAuNonPoBoxAddress
        ? null
        : { poBoxAddress: RelationshipTypeLabelsPlural[relationshipType] };
    };

    this.officeholdersForm.addValidators([officeholderPoBoxAddressValidator]);
  }

  private addOfficeholderPositionValidator(position: RelationshipType): void {
    const positionName = RelationshipTypeLabels[position];
    const errorKey = `selectAtLeastOne${ positionName }`;
    this.officeholdersPositionsCustomErrors[errorKey] = `At least one ${ positionName } must be selected.`;

    // valid if at least one officeholder type is same as position value
    const validator = (control: AbstractControl): ValidationErrors | null => {

      const officersFormArray = (control as FormArray<OfficeholderFormGroup>).controls;
      const allOfficersTypes = officersFormArray
        .flatMap((officerFormGroup) => officerFormGroup.controls.type.value);
      const allOfficersTypesSet = new Set(allOfficersTypes);

      return allOfficersTypesSet.has(position)
        ? null
        : { [errorKey]: true };
    };

    this.officeholdersForm.controls.officers.addValidators([validator]);
  }

  private fillStaticAddresses(): void {
    combineLatest(this.organisationService.getOrganisation(), this.settingsService.getAgentSettings())
      .subscribe(([organisation, agent]) => {
        if (organisation.address?.normalizedFullAddress)
          this.staticSuggestedAddressesRecord['organisationAddress'] = organisation.address;

        if (agent.postalAddress?.normalizedFullAddress)
          this.staticSuggestedAddressesRecord['agentPostalAddress'] = agent.postalAddress;

        if (agent.streetAddress?.normalizedFullAddress)
          this.staticSuggestedAddressesRecord['agentStreetAddress'] = agent.streetAddress;

        this.updateSuggestedAddresses();
      });
  }

  private updateSuggestedAddresses(): void {
    const addresses: Address[] = [];

    Object.values(this.staticSuggestedAddressesRecord)
      .forEach((address) => {
        if (address?.normalizedFullAddress)
          addresses.push(address);
      });

    if (this.addressForm.controls.principalAddress.valid && this.addressForm.controls.principalAddress.value.normalizedFullAddress)
      addresses.push(new Address(this.addressForm.controls.principalAddress.value as Partial<Address>));

    if (this.addressForm.controls.registeredAddressAndOccupier.controls.address.valid && this.addressForm.controls.registeredAddressAndOccupier.controls.address.value.normalizedFullAddress)
      addresses.push(new Address(this.addressForm.controls.registeredAddressAndOccupier.controls.address.value as Partial<Address>));

    Object.values(this.officeholdersForm.controls.officers.controls)
      .forEach((officerFormValue) => {
        if (officerFormValue.controls.address.valid && officerFormValue.controls.address.controls.normalizedFullAddress)
          addresses.push(new Address(officerFormValue.controls.address.value as Partial<Address>));
      });

    Object.values(this.shareholdersForm.controls.securityHolders.controls)
      .forEach((shareholderFormValue) => {
        if (shareholderFormValue.controls.registeredAddress.valid && shareholderFormValue.controls.registeredAddress.controls.normalizedFullAddress.value)
          addresses.push(new Address(shareholderFormValue.controls.registeredAddress.value as Partial<Address>));
        if (shareholderFormValue.controls.individualShareholder.controls.address.valid && shareholderFormValue.controls.individualShareholder.controls.address.controls.normalizedFullAddress.value)
          addresses.push(new Address(shareholderFormValue.controls.individualShareholder.controls.address.value as Partial<Address>));

        shareholderFormValue.controls.joint.value?.forEach((s) => {
          if ('individualData' in s && s.individualData.address.normalizedFullAddress)
            addresses.push(new Address(s.individualData.address as Partial<Address>));
          if ('registeredAddress' in s && s.registeredAddress)
            addresses.push(new Address(s.registeredAddress as Partial<Address>));
        });
      });

    const mappedAddresses = new Map<string, Address>();
    addresses.forEach((address) => mappedAddresses.set(address.normalizedFullAddress, address));

    this.suggestedAddresses = [...mappedAddresses.values()];
  }

  private updateSuggestedFullNames(): void {
    const nameOfOfficeholders = this.officeholdersForm.value.officers?.map((officeholder) => officeholder.fullName);
    const namesOfShareholders = this.shareholdersForm.value.securityHolders?.map((shareholder) => {
      if (shareholder.individualShareholder?.fullName)
        return [shareholder.individualShareholder.fullName];
      if (shareholder.name)
        return [shareholder.name];
      else
        return shareholder.joint?.map((s) => {
          if (s instanceof IndividualHolderModel)
            return s.individualData.fullName;
          else
            return null;
        });
    }).flat();

    this.suggestedNames = [...new Set([nameOfOfficeholders, namesOfShareholders].flat().filter(Boolean))] as string[];
  }

  private setupFormVariant(isSpecialPurpose = false): void {
    // Defines if it Company Standard Form or Special SMSF Company
    this.isSpecialPurpose = isSpecialPurpose;
    this.formHeader = isSpecialPurpose ? 'Special Purpose Company - SMSF Trustee' : 'Standard Company';
    this.currentStep = this.steps[this.currentStepIndex].step;
    this.checkIfItIsSimpleShareholdersFillingMode(this.formModel);
    this.fillOfficeholdersPositionOptions(isSpecialPurpose);

    this.officeholdersForm.controls.officers.push(this.createOfficeholderFormGroup({ type: RelationshipType.PublicOfficer }));
  }

  private fillOfficeholdersPositionOptions(isSpecialPurpose: boolean): void {
    this.firstOfficerCompanyStandardPositionTypesOptions = getCompanyRegistrationOfficersPositionTypesOptions(isSpecialPurpose, true);
    this.companyStandardPositionTypesOptions = getCompanyRegistrationOfficersPositionTypesOptions(isSpecialPurpose);
  }

  private getLegalElementsOptions(): SelectOption[] {
    return legalElements.proprietary.map((option) => ({
      ...option,
      value: (option.value as string).toUpperCase()
    }));
  }

  get companyDetailsForm() {
    return this.stepperForm.controls[CompanyStandardStepsEnum.CompanyDetails];
  }

  get addressForm() {
    return this.stepperForm.controls[CompanyStandardStepsEnum.Address];
  }

  get officeholdersForm() {
    return this.stepperForm.controls[CompanyStandardStepsEnum.Officeholders];
  }

  get shareholdersForm() {
    return this.stepperForm.controls[CompanyStandardStepsEnum.Shareholders];
  }

  get authorisedSignatoriesForm() {
    return this.stepperForm.controls[CompanyStandardStepsEnum.AuthorisedSignatories];
  }

  protected readonly standardCompanyDescription = standardCompanyDescription;
  protected readonly specialPurposeCompanyDescription = specialPurposeCompanyDescription;
}
